import { memo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Tooltip as ReactTooltip } from 'react-tooltip'

import HelpIcon from '@Icons/help-icon.svg?react'

import ProductOptions from './options'
import ProductCardTags from './ProductCardTags'
import AlertIcon from '../../assets/images/icons/alert-triangle.svg?react'
import CalendarButton from '../../assets/images/icons/calendar-blue.svg?react'
import FilledStar from '../../assets/images/icons/star-filled.svg?react'
import Star from '../../assets/images/icons/star.svg?react'
import TickGreen from '../../assets/images/icons/tick-green.svg?react'
import { useBookmarkMutation } from '../../store/api/product'
import type { PlanPackageType } from '../../store/types/billing'
import { PlatinumFeatures } from '../../store/types/billing'
import type { Product } from '../../store/types/product'
import { useNavigateBillingModal } from '../../utils/context/modal/use-navigate-billing-modal'
import SpinLoader from '../../utils/loaders/spin'
import Tooltip from '../../utils/tool-tip'

type Props = {
  product: Product
  currency?: string
  registerSource: string
  monitoringSource: string
  packageType: PlanPackageType
}

function ProductCardProductInfo({
  product,
  currency,
  registerSource,
  monitoringSource,
  packageType,
}: Props): JSX.Element {
  const { open } = useNavigateBillingModal()

  const [
    bookmarkRequest,
    { data: bookmarkResponse, isLoading: isBookmarkLoading },
  ] = useBookmarkMutation()

  const [showAllTags, setShowAllTags] = useState(false)

  const toggleShowAllTags = (): void => {
    setShowAllTags((current) => !current)
  }

  const handleBookmarkClick = async (): Promise<void> => {
    await bookmarkRequest(product.id)
  }

  const handleHistoryClick = (): void => {
    open('Product History')
  }

  return (
    <>
      <div className="flex justify-between items-start w-full">
        <div className="max-w-xs sm:max-w-sm lg:max-w-md xl:max-w-lg 2xl:max-w-xl">
          <h1 className="font-medium text-lg text-gray-900 leading-7 mr-4">
            {product.name}
          </h1>
        </div>
        <div className="flex flex-row items-center gap-1">
          <Tooltip text="See product information over time">
            {PlatinumFeatures.includes(packageType) ? (
              <Link to={`/products/history/${product.id}`}>
                <button className="flex items-center pr-3">
                  <CalendarButton />
                  <span className="font-medium text-base text-primary-600 pl-2">
                    History
                  </span>
                </button>
              </Link>
            ) : (
              <button
                type="button"
                onClick={handleHistoryClick}
                className="flex items-center pr-3"
              >
                <CalendarButton />
                <span className="font-medium text-base text-primary-600 pl-2">
                  History
                </span>
              </button>
            )}
          </Tooltip>
          <Tooltip text="Add to favorites">
            <button type="button" onClick={handleBookmarkClick}>
              {isBookmarkLoading ? (
                <span className="[&>svg]:w-[20px]">
                  <SpinLoader />
                </span>
              ) : (bookmarkResponse ?? product).is_favorite ? (
                <FilledStar />
              ) : (
                <Star />
              )}
            </button>
          </Tooltip>
          <ProductOptions product={product} />
        </div>
      </div>
      <div className="flex flex-col text-sm font-medium pt-4 gap-y-2">
        <div className="flex flex-row">
          <h3 className={`text-gray-500 w-1/3 flex-shrink-0 lg:w-[108px]`}>
            Barcode:{' '}
          </h3>
          {product.barcode ? (
            <div className="text-gray-900 flex">
              <span className="pr-2">{product.barcode}</span>
              {monitoringSource !== 'PRISYNC_ENGINE' &&
                product.is_barcode_valid === false && (
                  <>
                    <AlertIcon
                      className="inline mr-1 align-bottom w-[1.15em]"
                      data-tooltip-id={`barcode-tooltip-${product.id}`}
                    />
                    <ReactTooltip id={`barcode-tooltip-${product.id}`}>
                      <div>Invalid Barcode!</div>
                    </ReactTooltip>
                  </>
                )}
            </div>
          ) : (
            <></>
          )}
        </div>
        <div className="flex flex-row">
          <h3 className={`text-gray-500 w-1/3 flex-shrink-0 lg:w-[108px]`}>
            Brand:{' '}
          </h3>
          <span className=" text-gray-900">{product.brand?.name}</span>
        </div>
        <div className="flex flex-row">
          <h3 className={`text-gray-500 w-1/3 flex-shrink-0 lg:w-[108px]`}>
            Category:{' '}
          </h3>
          <span className="text-gray-900">{product.category?.name}</span>
        </div>
        <div className="flex flex-row">
          <h3 className={`text-gray-500 w-1/3 flex-shrink-0 lg:w-[108px]`}>
            Product Code:{' '}
          </h3>
          <span className="text-gray-900">{product.product_code}</span>
        </div>
        {registerSource === 'Shopify' && product.tracker[0]?.shopifyStatus && (
          <div className="flex flex-row items-center">
            <h3 className={`text-gray-500 w-1/3 flex-shrink-0 lg:w-[108px]`}>
              Status:
            </h3>
            <span className="text-gray-900">
              {product?.ProductExternalReference?.is_deleted ? (
                <div className="flex align-middle">
                  <span className="mr-1">
                    This product is no longer available on Shopify
                  </span>{' '}
                  <Tooltip
                    text="This product is no longer available on Shopify and won't sync. 
                  It may have been deleted or turned into a variant product. 
                  You can also delete this product from the Prisync panel."
                  >
                    <HelpIcon />
                  </Tooltip>
                </div>
              ) : (
                product.tracker[0]?.shopifyStatus
              )}
            </span>
          </div>
        )}
        <div className="flex flex-row">
          <h3 className={`text-gray-500 w-1/3 flex-shrink-0 lg:w-[108px]`}>
            Product Cost:{' '}
          </h3>
          <span className="text-gray-900">
            {product.cost} {currency && currency}
          </span>
        </div>
        <div className="flex flex-row">
          <h3 className={`text-gray-500 w-1/3 flex-shrink-0 lg:w-[108px]`}>
            Additional Cost:{' '}
          </h3>
          <span className="text-gray-900">
            {product.additional_cost} {currency && currency}
          </span>
        </div>
        {product.product_tag.length > 0 ? (
          <ProductCardTags
            tags={product.product_tag}
            toggleShowAll={toggleShowAllTags}
            showAll={showAllTags}
          />
        ) : null}
      </div>
    </>
  )
}

const MemoProductCardProductInfo = memo(ProductCardProductInfo)

export default MemoProductCardProductInfo
